// Easter eggs (02-EASTER-EGGS.md). Los textos son finales: se copian tal cual del
// documento. Lo que no está en el documento (cómo desactivar Midnight City, las
// etiquetas del buscador y del panel de atajos) lo escribimos con las reglas de la
// guía, sección 9. Los textos de la terminal viven en terminal.ts.

// Interruptores de los secretos que tocan algo interno (02-EASTER-EGGS.md, 1.10).
// Quedan encendidos y listados en la bitácora hasta que Juan Manuel los apruebe.
export const easterSwitches = {
  // 🔵 3.7 · Quinto es el perro de un integrante del equipo: guiño interno, esperar aprobación.
  konami: true,
  // 🔵 3.9 · Agrega la letra chica al copiar el claim: modifica el portapapeles, aprobar antes de publicar.
  copyClaim: true,
};

// ---- Contador de secretos (7) ----

export type SecretId =
  | "terminal"
  | "florecer"
  | "consola"
  | "404"
  | "midnight"
  | "salvapantallas"
  | "konami"
  | "ajedrez"
  | "rmrf"
  | "dato"
  | "umbral"
  | "humans";

const ALL_SECRETS: SecretId[] = [
  "terminal",
  "florecer",
  "consola",
  "404",
  "midnight",
  "salvapantallas",
  "konami",
  "ajedrez",
  "rmrf",
  "dato",
  "umbral",
  "humans",
];

/** Los que cuentan: Konami solo si está encendido ("si está aprobado", 7). */
export const countedSecrets: SecretId[] = ALL_SECRETS.filter((s) => s !== "konami" || easterSwitches.konami);

export const secretsText = {
  progress: (n: number, total: number) => `Encontraste ${n} de ${total} secretos.`,
  all: "Los encontraste todos. Ya sos parte del equipo, aunque todavía no lo sepamos.",
};

// ---- Mensajes breves (avisos) ----

export const toasts = {
  bloom: "7 vueltas. El óptimo.",
  midnightOn: "Modo Midnight City activado.",
  midnightOnHow: "Para volver, escribí «midnight» otra vez.",
  midnightOff: "Modo Midnight City desactivado.",
  konami: "Quinto pasó a saludar.",
  copyClaim: "Copiado con su letra chica. Nos importa que viaje completa.",
};

/** Lo que se agrega al copiar el claim (3.9). */
export const claimSuffix = "(hasta donde pudimos verificar · yvyrai.lat/auditoria)";

// ---- Consola del navegador (3.2) ----

// El árbol del logo en ASCII: tronco con pistas de circuito que se abren en ramas y
// terminan en nodos (o), como el dibujo del logo.
export const consoleTree = String.raw`
                  o   o   o
           o      |   |   |      o
        o   \     |   |   |     /   o
      o  \   \____|   |   |____/   /  o
    o  \  \       \   |   /       /  /  o
     \__\__\___    \  |  /    ___/__/__/
  o_________   \____\ | /____/   _________o
    o_______\_______ \|/ _______/_______o
                    |||
                    |||
                    |||
                    |||
       o_______    / | \    _______o
         o_____\__/  |  \__/_____o
              o__/   |   \__o
                 o   o   o
`;

export const consoleMessage = [
  "Si estás leyendo esto, probablemente seas de los nuestros.",
  "Construimos un modelo de lenguaje desde cero en Asunción.",
  "Si querés construir con nosotros: admin@yvyrai.lat · asunto «Consola»",
  "",
  "Pista: hay más cosas escondidas. Empezá por mirar el código fuente.",
];

// ---- 404 (3.3) ----

export const notFoundText = {
  title: "Esta página no existe.",
  body: "Nuestro modelo a veces inventa cosas. Esta vez inventamos una dirección.",
  home: "Volver al inicio",
  all: "Ver todas las páginas",
};

// ---- El desafío escondido (5) ----

export const ctf = {
  sourceComment: "<!-- ¿Cuántas vueltas necesita para dejar de dudar? La respuesta abre el umbral. -->",
  question: "¿Qué tiene de propio este modelo?",
  answers: ["arquitectura", "la arquitectura", "deliberationblock"],
  wrong: "Todavía no.",
  /** Lo que dice la cadena en base64 que aparece con la respuesta correcta. */
  decoded: "abrí la terminal y escribí: umbral 7",
  submit: "Responder",
  fieldLabel: "Tu respuesta",
};

// ---- Ajedrez (3.8) ----

export const chessText = {
  title: "Ajedrez",
  notModel: "Juega un libro de aperturas, no el modelo.",
  yourTurn: "Jugás con blancas.",
  thinking: "Las negras responden.",
  bookEnd: "Hasta acá llega nuestro libro de aperturas. Lo demás es tuyo.",
  freePlay: "Ahora movés las dos.",
  restart: "Empezar de nuevo",
  close: "Cerrar",
  check: "Jaque.",
  mate: "Jaque mate.",
  stalemate: "Tablas por ahogado.",
};

// ---- Dato dado vuelta (3.11) ----

export const rawText = {
  message: "Este es el número de verdad.",
  chart: "Ver el gráfico completo",
  chartHref: "/investigacion#resultados",
  close: "Volver",
};

/**
 * Fragmentos reales de referencia/datos/depth_sweep_reasoning.json → curves.forced,
 * copiados tal cual (solo sin la sangría del archivo). Si el JSON cambia, hay que volver
 * a copiarlos. El −29,5 % del inicio sale de k = 1 y k = 7:
 * (49,2767 − 69,8600) / 69,8600 = −29,46 %.
 */
export const rawSource = { file: "depth_sweep_reasoning.json", path: "curves.forced" };

export const rawForced: Record<number, { ppl: number; text: string }> = {
  1: {
    ppl: 69.8600082397461,
    text: `"1": {
  "loss": 4.246493394718762,
  "ppl": 69.8600082397461,
  "bpc": 6.126394961728497,
  "tokens": 33477,
  "avg_actual_iters": 1.0
}`,
  },
  2: {
    ppl: 54.941314697265625,
    text: `"2": {
  "loss": 4.006265791071342,
  "ppl": 54.941314697265625,
  "bpc": 5.779819789261725,
  "tokens": 33477,
  "avg_actual_iters": 2.0
}`,
  },
  3: {
    ppl: 51.309661865234375,
    text: `"3": {
  "loss": 3.9378791629269254,
  "ppl": 51.309661865234375,
  "bpc": 5.6811587399746575,
  "tokens": 33477,
  "avg_actual_iters": 3.0
}`,
  },
  4: {
    ppl: 50.00934982299805,
    text: `"4": {
  "loss": 3.912210104795013,
  "ppl": 50.00934982299805,
  "bpc": 5.644126117103458,
  "tokens": 33477,
  "avg_actual_iters": 4.0
}`,
  },
  5: {
    ppl: 49.464599609375,
    text: `"5": {
  "loss": 3.9012571594017675,
  "ppl": 49.464599609375,
  "bpc": 5.628324357101494,
  "tokens": 33477,
  "avg_actual_iters": 5.0
}`,
  },
  6: {
    ppl: 49.30112075805664,
    text: `"6": {
  "loss": 3.8979468923509986,
  "ppl": 49.30112075805664,
  "bpc": 5.623548651243332,
  "tokens": 33477,
  "avg_actual_iters": 6.0
}`,
  },
  7: {
    ppl: 49.276668548583984,
    text: `"7": {
  "loss": 3.8974506656097376,
  "ppl": 49.276668548583984,
  "bpc": 5.6228327473845585,
  "tokens": 33477,
  "avg_actual_iters": 7.0
}`,
  },
  8: {
    ppl: 49.281131744384766,
    text: `"8": {
  "loss": 3.897541342298094,
  "ppl": 49.281131744384766,
  "bpc": 5.622963566193174,
  "tokens": 33477,
  "avg_actual_iters": 8.0
}`,
  },
  // De 9 a 16 el archivo repite el mismo valor (la meseta); solo cambia avg_actual_iters.
  ...Object.fromEntries(
    [9, 10, 11, 12, 13, 14, 15, 16].map((k) => [
      k,
      {
        ppl: 49.28120040893555,
        text: `"${k}": {
  "loss": 3.8975426057734133,
  "ppl": 49.28120040893555,
  "bpc": 5.622965389002752,
  "tokens": 33477,
  "avg_actual_iters": ${k}.0
}`,
      },
    ]),
  ),
};

// ---- Atajos (3.10) ----

export const shortcutsText = {
  title: "Atajos de teclado",
  rows: [
    { keys: ["Tab"], label: "Recorrer los enlaces y controles de la página" },
    { keys: ["Ctrl", "K"], label: "Buscar en el sitio (⌘ K en Mac)" },
    { keys: ["la tecla debajo de Esc"], label: "Abrir la terminal" },
    { keys: ["?"], label: "Mostrar estos atajos" },
    { keys: ["Esc"], label: "Cerrar lo que esté abierto" },
  ],
  close: "Cerrar",
};

// ---- Buscador (3.10) ----

export const searchText = {
  title: "Buscar en el sitio",
  placeholder: "Buscá páginas, preguntas, modelos o datos",
  empty: "No encontramos nada con eso. Probá con otra palabra.",
  hint: "↑ ↓ para elegir · Enter para abrir · Esc para cerrar",
  close: "Cerrar",
  groups: { page: "Página", faq: "Pregunta frecuente", model: "Modelo", product: "Producto", data: "Dato" },
  results: (n: number) => (n === 1 ? "1 resultado" : `${n} resultados`),
};

// ---- Impresión (6.5) ----

export const printText = {
  foot: "Impreso desde yvyrai.lat",
};
